import { animate } from "motion/mini"

// Fires callback once when element enters viewport
function onScrollReveal(selector, callback, threshold) {
  const el = document.querySelector(selector)
  if (!el) return
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect()
        callback(el)
      }
    },
    { threshold: threshold || 0.3 }
  )
  observer.observe(el)
}

// Runs fn on interval while el is visible, pauses when offscreen
function visibilityLoop(el, fn, ms) {
  let intervalId = null
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        if (!intervalId) intervalId = setInterval(fn, ms)
      } else {
        clearInterval(intervalId)
        intervalId = null
      }
    },
    { threshold: 0.1 }
  )
  observer.observe(el)
}

function stagger(els, props, baseOpts, delayStep) {
  Array.from(els).forEach((el, i) => {
    animate(el, props, { ...baseOpts, delay: (baseOpts.delay || 0) + i * delayStep })
  })
}

// ─── Bento: Unified Inbox ──────────────────────────────────

function animateBentoInbox(el) {
  const rows = el.querySelectorAll(".bento-preview .flex")
  stagger(rows,
    { opacity: [0, 1], transform: ["translateX(-10px)", "translateX(0)"] },
    { duration: 0.35, easing: "ease-out" },
    0.12
  )

  // Loop: highlight top row like a new message arriving
  visibilityLoop(el, () => {
    const first = rows[0]
    if (!first) return
    animate(first,
      { backgroundColor: ["#ffffff", "#eff6ff", "#ffffff"] },
      { duration: 1.2, easing: "ease-in-out" }
    )
  }, 4000)
}

// ─── Bento: Order Management ───────────────────────────────

function animateBentoOrders(el) {
  const rows = el.querySelectorAll(".bento-preview > div > div")
  const dots = el.querySelectorAll(".bento-preview .rounded-full")
  // Inner colored bars (inside the overflow-hidden track)
  const bars = el.querySelectorAll(".bento-preview .overflow-hidden > .h-2")

  // Rows fade in
  stagger(rows, { opacity: [0, 1] }, { duration: 0.3, easing: "ease-out" }, 0.12)

  // Status dots pop in with spring
  Array.from(dots).forEach((dot, i) => {
    animate(dot,
      { opacity: [0, 1], transform: ["scale(0)", "scale(1)"] },
      { duration: 0.3, delay: 0.15 + i * 0.15, easing: [0.34, 1.56, 0.64, 1] }
    )
  })

  // Progress bars grow from left
  Array.from(bars).forEach((bar, i) => {
    bar.style.transformOrigin = "left"
    animate(bar,
      { transform: ["scaleX(0)", "scaleX(1)"] },
      { duration: 0.5, delay: 0.2 + i * 0.15, easing: [0.34, 1.2, 0.64, 1] }
    )
  })

  // Loop: "Refining" bar pulses slightly wider
  visibilityLoop(el, () => {
    const refiningBar = bars[1]
    if (!refiningBar) return
    animate(refiningBar,
      { transform: ["scaleX(1)", "scaleX(1.04)", "scaleX(1)"] },
      { duration: 1.5, easing: "ease-in-out" }
    )
  }, 5000)
}

// ─── Bento: Accounting Sync ────────────────────────────────

function animateBentoSync(el) {
  const tableRows = el.querySelectorAll(".bento-preview .flex.justify-between")
  stagger(tableRows,
    { opacity: [0, 1], transform: ["translateX(10px)", "translateX(0)"] },
    { duration: 0.3, easing: "ease-out" },
    0.12
  )

  // Typewriter on "Synced" text
  const syncedSpans = el.querySelectorAll(".bento-preview [style*='color: #10b981']")
  syncedSpans.forEach((span, i) => {
    const text = span.textContent
    span.textContent = ""
    span.style.fontFamily = "ui-monospace, monospace"
    let charIdx = 0
    setTimeout(() => {
      const typeInterval = setInterval(() => {
        span.textContent = text.slice(0, ++charIdx)
        if (charIdx >= text.length) clearInterval(typeInterval)
      }, 60)
    }, 400 + i * 200)
  })

  // Loop: green pulse on status text
  visibilityLoop(el, () => {
    syncedSpans.forEach(span => {
      animate(span,
        { opacity: [1, 0.5, 1] },
        { duration: 0.8, easing: "ease-in-out" }
      )
    })
  }, 5000)
}

// ─── Problem Cards ────────────────────────────────────────

function animateProblemCards(el) {
  const cards = el.children

  // Card 1: Scattered orders — icon boxes wobble in with spring
  const iconBoxes = cards[0].querySelectorAll(".h-20 > div")
  const rotations = ["-5deg", "8deg", "-3deg"]
  Array.from(iconBoxes).forEach((box, i) => {
    animate(box,
      { opacity: [0, 1], transform: [`scale(0.5) rotate(${rotations[i]})`, `scale(1) rotate(${rotations[i]})`] },
      { duration: 0.4, delay: i * 0.12, easing: [0.34, 1.56, 0.64, 1] }
    )
  })

  // Card 1 loop: slight drift jitter
  visibilityLoop(cards[0], () => {
    Array.from(iconBoxes).forEach((box, i) => {
      const dx = (Math.random() - 0.5) * 6
      const dy = (Math.random() - 0.5) * 4
      animate(box,
        { transform: [
          `translate(0, 0) rotate(${rotations[i]})`,
          `translate(${dx}px, ${dy}px) rotate(${rotations[i]})`,
          `translate(0, 0) rotate(${rotations[i]})`
        ] },
        { duration: 1.2, easing: "ease-in-out" }
      )
    })
  }, 3000)

  // Card 2: Conversation gap — bubbles slide in from alternating sides
  cards[1].querySelector(".relative").style.opacity = "1"
  const bubbles = cards[1].querySelectorAll(".absolute")
  Array.from(bubbles).forEach((bubble, i) => {
    const fromX = i % 2 === 0 ? "-20px" : "20px"
    animate(bubble,
      { opacity: [0, 1], transform: [`translateX(${fromX})`, "translateX(0)"] },
      { duration: 0.35, delay: i * 0.2, easing: "ease-out" }
    )
  })

  // Card 3: Manual invoicing — document fades in, sync badge spins
  cards[2].querySelector(".relative").style.opacity = "1"
  const doc = cards[2].querySelector(".bg-blue-50")
  const syncBadge = cards[2].querySelector(".rounded-full")

  animate(doc,
    { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
    { duration: 0.4, easing: "ease-out" }
  )

  animate(syncBadge,
    { opacity: [0, 1], transform: ["rotate(0deg)", "rotate(360deg)"] },
    { duration: 0.6, delay: 0.3, easing: [0.34, 1.2, 0.64, 1] }
  )

  // Card 3 loop: sync badge re-spins every 4s
  visibilityLoop(cards[2], () => {
    animate(syncBadge,
      { transform: ["rotate(0deg)", "rotate(360deg)"] },
      { duration: 0.6, easing: [0.34, 1.2, 0.64, 1] }
    )
  }, 4000)
}

// ─── Init ──────────────────────────────────────────────────

export function initScrollAnimations() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

  onScrollReveal("[data-animate='problem-cards']", animateProblemCards, 0.4)
  onScrollReveal("[data-animate='bento-inbox']", animateBentoInbox)
  onScrollReveal("[data-animate='bento-orders']", animateBentoOrders)
  onScrollReveal("[data-animate='bento-sync']", animateBentoSync)
}
